import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import moment from 'moment'
import type {
  OperationLogBatchDetailType,
  OperationLogBatchSummaryType,
  OperationLogFiltersType,
  OperationLogLegacyFiltersType,
  OperationLogListResponse,
  OperationLogType,
} from '@/models/operation-log'
import { fetchOperationLogBatchDetail, fetchOperationLogLegacyList, fetchOperationLogV2List } from './operation-log-thunks'

/** 一覧 URL 未指定時と slice 初期値で共通利用 */
export const getDefaultOperationLogV2Filters = (): OperationLogFiltersType => {
  const end = moment().endOf('day').format('YYYY-MM-DD HH:mm:ss')
  const begin = moment().subtract(7, 'days').startOf('day').format('YYYY-MM-DD HH:mm:ss')
  return { beginDate: begin, endDate: end }
}

const getDefaultLegacyFilters = (): OperationLogLegacyFiltersType => ({})

export type OperationLogListStatus = 'idle' | 'loading' | 'succeeded' | 'failed'

type OperationLogState = {
  list: OperationLogBatchSummaryType[]
  listStatus: OperationLogListStatus
  listError: unknown | undefined
  listMeta: OperationLogListResponse['meta'] | undefined
  listLinks: OperationLogListResponse['links'] | undefined
  perPage: number
  filters: OperationLogFiltersType
  page: number
  openedBatchUuid: string | null
  detailsByBatchUuid: Record<string, OperationLogBatchDetailType | null>
  detailInFlight: Record<string, boolean>
  legacyList: OperationLogType[]
  legacyListStatus: OperationLogListStatus
  legacyListError: unknown | undefined
  legacyListMeta: OperationLogListResponse['meta'] | undefined
  legacyListLinks: OperationLogListResponse['links'] | undefined
  legacyPerPage: number
  legacyFilters: OperationLogLegacyFiltersType
  legacyPage: number
}

const initialState: OperationLogState = {
  list: [],
  listStatus: 'idle',
  listError: undefined,
  listMeta: undefined,
  listLinks: undefined,
  perPage: 50,
  filters: getDefaultOperationLogV2Filters(),
  page: 1,
  openedBatchUuid: null,
  detailsByBatchUuid: {},
  detailInFlight: {},
  legacyList: [],
  legacyListStatus: 'idle',
  legacyListError: undefined,
  legacyListMeta: undefined,
  legacyListLinks: undefined,
  legacyPerPage: 50,
  legacyFilters: getDefaultLegacyFilters(),
  legacyPage: 1,
}

export const operationLogSlice = createSlice({
  name: 'operationLog',
  initialState,
  reducers: {
    setOpenedBatchUuid: (state, action: PayloadAction<string | null>) => {
      state.openedBatchUuid = action.payload
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchOperationLogV2List.pending, state => {
        state.listStatus = 'loading'
        state.listError = undefined
        state.detailsByBatchUuid = {}
        state.detailInFlight = {}
      })
      .addCase(fetchOperationLogV2List.fulfilled, (state, action) => {
        state.listStatus = 'succeeded'
        state.list = action.payload.data ?? []
        state.listMeta = action.payload.meta
        state.listLinks = action.payload.links
        state.filters = action.meta.arg.filters
        state.page = action.meta.arg.page
        state.perPage = action.payload.meta?.per_page ?? action.meta.arg.limit
      })
      .addCase(fetchOperationLogV2List.rejected, (state, action) => {
        state.listStatus = 'failed'
        state.listError = action.payload ?? action.error
      })
      .addCase(fetchOperationLogLegacyList.pending, state => {
        state.legacyListStatus = 'loading'
        state.legacyListError = undefined
      })
      .addCase(fetchOperationLogLegacyList.fulfilled, (state, action) => {
        state.legacyListStatus = 'succeeded'
        state.legacyList = action.payload.data ?? []
        state.legacyListMeta = action.payload.meta
        state.legacyListLinks = action.payload.links
        state.legacyFilters = action.meta.arg.filters
        state.legacyPage = action.meta.arg.page
        state.legacyPerPage = action.payload.meta?.per_page ?? action.meta.arg.limit
      })
      .addCase(fetchOperationLogLegacyList.rejected, (state, action) => {
        state.legacyListStatus = 'failed'
        state.legacyListError = action.payload ?? action.error
      })
      .addCase(fetchOperationLogBatchDetail.pending, (state, action) => {
        const { batchUuid } = action.meta.arg
        state.detailInFlight[batchUuid] = true
      })
      .addCase(fetchOperationLogBatchDetail.fulfilled, (state, action) => {
        const { batchUuid, data } = action.payload
        state.detailsByBatchUuid[batchUuid] = data
        state.detailInFlight[batchUuid] = false
      })
      .addCase(fetchOperationLogBatchDetail.rejected, (state, action) => {
        const batchUuid = action.meta.arg?.batchUuid
        if (batchUuid) {
          state.detailInFlight[batchUuid] = false
        }
      })
  },
})

export const { setOpenedBatchUuid } = operationLogSlice.actions
export const operationLogReducer = operationLogSlice.reducer
